/**
 * 매뉴얼 찾아 읽기
 *
 * 왜 필요한가:
 *   도우미는 ERP 데이터는 SP 로 읽지만 "이거 어떻게 해요" 는 읽을 데가 없었다. 그래서 기억으로
 *   답했고, 기억에는 이 회사 화면이 없다. 버튼 이름과 순서를 그럴듯하게 지어내면 사용자는 그
 *   버튼을 찾다가 시간을 버린다. 틀린 답 중에서도 나쁜 축이다.
 *
 *   매뉴얼은 이미 public/manuals/*.html 에 있다. 사람이 읽으라고 쓴 글이라 그대로 근거가 된다.
 *   그래서 도우미가 답하기 전에 여기서 해당 장을 찾아 읽게 한다.
 *
 * 왜 프롬프트에 통째로 넣지 않는가:
 *   매뉴얼 7권이 HTML 420KB, 글만 추려도 수만 토큰이다. 매 턴 실어 나르면 질문 하나 값이 그만큼
 *   오른다. 대부분의 질문은 매뉴얼과 상관없다. 그래서 목록(제목 몇 줄)만 프롬프트에 두고,
 *   필요할 때 그 장만 가져간다.
 *
 * 어떻게 쪼개나:
 *   매뉴얼 HTML 은 <main> 안에 <section id="cN"> 으로 장이 나뉘어 있고 각 장에 <h2> 제목이 있다.
 *   그 단위가 사람이 "몇 장을 보세요" 라고 말하는 단위라 그대로 검색 단위로 쓴다.
 *   자주 묻는 질문의 <summary> 는 질문이므로 "Q." 를 붙여 답과 구분해 둔다 — 검색에서 가장 잘 맞는
 *   부분이 거기인 경우가 많다.
 */

import { MANUALS, MANUAL_STATUS, getManualFileUrl, findManual } from '../../config/manuals';

/** 줄바꿈을 만들어야 하는 태그들 */
const BLOCK_TAGS = new Set([
  'P', 'LI', 'H2', 'H3', 'H4', 'DIV', 'SECTION', 'DETAILS', 'SUMMARY',
  'UL', 'OL', 'TR', 'TABLE', 'BLOCKQUOTE', 'FIGCAPTION', 'DT', 'DD',
]);

/** 본문에서 통째로 빼는 태그들 */
const DROP_TAGS = new Set(['SCRIPT', 'STYLE', 'NAV', 'NOSCRIPT']);

/** 매뉴얼 한 권당 한 번만 읽는다. { id → Promise<Section[]> } */
const cache = new Map();

/** 한 장에서 넘겨줄 글자 수 상한 — 이보다 길면 잘라서 준다 */
const MAX_SECTION_CHARS = 6000;

// ── 파싱 ─────────────────────────────────────────────────

/** 요소 안의 글을 사람이 읽는 줄글로 편다 */
function blockText(el) {
  const parts = [];
  const walk = (node) => {
    if (node.nodeType === 3) {
      parts.push(String(node.nodeValue || '').replace(/\s+/g, ' '));
      return;
    }
    if (node.nodeType !== 1) return;
    const tag = node.tagName;
    if (DROP_TAGS.has(tag)) return;
    const isBlock = BLOCK_TAGS.has(tag);
    if (isBlock) parts.push('\n');
    if (tag === 'SUMMARY') parts.push('Q. ');
    node.childNodes.forEach(walk);
    if (isBlock) parts.push('\n');
  };
  walk(el);
  return parts
    .join('')
    .replace(/[ \t ]+/g, ' ')
    .replace(/ *\n */g, '\n')
    .replace(/\n{2,}/g, '\n')
    .trim();
}

/**
 * 매뉴얼 HTML 을 장 단위로 쪼갠다.
 * 테스트가 이 함수만 부를 수 있게 fetch 와 분리해 두었다.
 */
export function parseManualHtml(html) {
  const doc = new DOMParser().parseFromString(String(html || ''), 'text/html');
  const sections = [];

  // 머리말(리드 + "먼저 이것만")은 장 밖에 있지만 요약이라 검색 가치가 크다.
  const masthead = doc.querySelector('header.masthead');
  if (masthead) {
    const 본문 = blockText(masthead);
    if (본문) sections.push({ 장id: '', 번호: '', 제목: '먼저 이것만', 본문 });
  }

  const nodes = doc.querySelectorAll('main section[id], section[id]');
  const seen = new Set();
  nodes.forEach((el) => {
    const id = el.getAttribute('id');
    if (!id || seen.has(id)) return;
    seen.add(id);
    const 번호 = (el.querySelector('.sec-num')?.textContent || '').trim();
    const 제목 = (el.querySelector('h2')?.textContent || '').trim().replace(/^\d+\s*/, '');
    const 본문 = blockText(el);
    if (!본문) return;
    sections.push({ 장id: id, 번호, 제목: 제목 || id, 본문 });
  });

  return sections;
}

// ── 읽어오기 ──────────────────────────────────────────────

/** 매뉴얼 한 권을 읽어 장 목록으로 만든다. 같은 권은 한 번만 읽는다. */
export async function loadManualSections(id) {
  if (cache.has(id)) return cache.get(id);
  const promise = (async () => {
    const res = await fetch(getManualFileUrl(id));
    if (!res.ok) throw new Error(`매뉴얼을 불러오지 못했습니다 (HTTP ${res.status})`);
    return parseManualHtml(await res.text());
  })();
  // 실패하면 캐시에 남기지 않는다 — 일시적인 실패로 그 매뉴얼이 영영 안 읽히면 안 된다.
  promise.catch(() => cache.delete(id));
  cache.set(id, promise);
  return promise;
}

/** 캐시를 비운다 (테스트용) */
export function resetManualCache() {
  cache.clear();
}

/** 읽을 수 있는 매뉴얼들 */
const readyManuals = () => MANUALS.filter((m) => m.status === MANUAL_STATUS.READY);

// ── 검색 ─────────────────────────────────────────────────

/** 흔한 조사 — 붙어 있으면 떼고 찾는다. "재고구분을" 로는 아무것도 안 걸린다. */
// 긴 것부터 본다 — '이랑' 을 '랑' 으로 떼면 '예약이' 같은 반쪽이 남는다.
const PARTICLES = ['에서는', '으로는', '에서', '으로', '까지', '부터', '한테', '에게', '이나', '라도', '이랑', '처럼', '보다', '마다', '은', '는', '이', '가', '을', '를', '에', '로', '의', '도', '만', '와', '과', '랑'];

/**
 * 묻는 말 자체는 검색에서 뺀다.
 * "취소료는 어디서 바꾸나요" 가 '어디서' 때문에 "어디서 시작하나" 장을 1등으로 물어온 적이 있다.
 * 모든 사용법 질문에 들어가는 말이라 변별력이 없으면서 제목에 우연히 있으면 크게 이긴다.
 */
const STOPWORDS = new Set([
  '어떻게', '어떡해', '어디서', '어디', '어떤', '언제', '누가', '얼마나', '무엇', '무슨', '뭐가', '뭔가요', '뭐예요', '뭐임',
  '하나요', '있나요', '되나요', '합니까', '하려면', '하고싶어요', '싶어요', '주세요', '알려줘', '알려주세요',
  '설명', '궁금', '가능한가요', '그리고', '이거', '그거', '저거',
]);

/**
 * 매뉴얼이 쓰는 말 ↔ 사람이 쓰는 말.
 *
 * 매뉴얼은 일부러 쉬운 말로 쓰여 있다. 입금 장의 제목은 "돈 받았을 때" 다. 그래서 "입금 처리
 * 어떻게 해요" 로는 그 장이 제목에서 안 걸리고 엉뚱한 장이 이긴다. 실제로 그랬다.
 * 여기 적은 것은 그런 어긋남이 확인된 낱말들뿐이다. 추측으로 늘리지 말 것.
 */
const SYNONYMS = [
  { 업무말: ['입금', '수납', '입금처리', '결제'], 매뉴얼말: ['돈 받'] },
  { 업무말: ['지출', '비용', '전도금', '출금'], 매뉴얼말: ['돈 썼'] },
  { 업무말: ['미수', '미수금'], 매뉴얼말: ['못 받'] },
  { 업무말: ['고객', '예약자'], 매뉴얼말: ['손님'] },
  { 업무말: ['재고', '좌석'], 매뉴얼말: ['자리'] },
  { 업무말: ['블록', '하드블록', '소프트블록'], 매뉴얼말: ['블럭', '공유재고'] },
];

/** 질문을 검색어 조각으로 자른다 */
export function toTokens(query) {
  const words = String(query || '')
    .replace(/[?!.,·…"'()[\]]/g, ' ')
    .split(/\s+/)
    .filter(Boolean);

  const out = new Set();
  for (const w of words) {
    if (w.length < 2) continue;
    const lower = w.toLowerCase();
    if (STOPWORDS.has(lower)) continue;
    out.add(lower);
    for (const p of PARTICLES) {
      if (w.length > p.length + 1 && w.endsWith(p)) {
        const stem = lower.slice(0, -p.length);
        if (!STOPWORDS.has(stem)) out.add(stem);
        break;
      }
    }
  }

  // 업무 용어로 물었으면 매뉴얼이 쓰는 쉬운 말도 같이 찾는다
  for (const { 업무말, 매뉴얼말 } of SYNONYMS) {
    if (업무말.some((w) => out.has(w))) 매뉴얼말.forEach((w) => out.add(w));
  }

  return [...out];
}

/** 문자열 안에 조각이 몇 번 나오는지 */
function countOf(haystack, needle) {
  if (!needle) return 0;
  let n = 0;
  let from = 0;
  for (;;) {
    const at = haystack.indexOf(needle, from);
    if (at < 0) return n;
    n += 1;
    from = at + needle.length;
  }
}

/**
 * 장 하나의 점수.
 * 제목에 있으면 크게 준다 — 매뉴얼은 장 제목이 곧 그 화면의 일이라 제목이 맞으면 대개 그 장이 맞다.
 * 본문 반복은 3번까지만 센다. 안 그러면 긴 장이 길다는 이유로 이긴다.
 */
export function scoreSection({ manualTitle, 제목, 본문 }, tokens) {
  const t = String(제목 || '').toLowerCase();
  const b = String(본문 || '').toLowerCase();
  const m = String(manualTitle || '').toLowerCase();
  let score = 0;
  for (const tok of tokens) {
    if (t.includes(tok)) score += 8;
    if (m.includes(tok)) score += 3;
    score += Math.min(3, countOf(b, tok)) * 2;
  }
  return score;
}

/** 가장 잘 맞는 조각 주변을 잘라 보여준다 */
function excerptAround(본문, tokens, span = 150) {
  const lower = 본문.toLowerCase();
  let at = -1;
  for (const tok of tokens) {
    const i = lower.indexOf(tok);
    if (i >= 0 && (at < 0 || i < at)) at = i;
  }
  if (at < 0) return 본문.slice(0, span * 2).trim();
  const start = Math.max(0, at - span);
  const end = Math.min(본문.length, at + span);
  return `${start > 0 ? '…' : ''}${본문.slice(start, end).trim()}${end < 본문.length ? '…' : ''}`;
}

/** 사용자가 눌러서 열 수 있는 앱 안 주소 */
export const manualLink = (manualId, 장id) => `/manuals/${manualId}${장id ? `?c=${장id}` : ''}`;

/**
 * 매뉴얼 전체에서 질문에 맞는 장을 찾는다.
 * 못 읽은 매뉴얼이 있어도 나머지로 답한다 — 한 권이 안 읽힌다고 전부 못 찾을 이유가 없다.
 */
export async function searchManual(query, { limit = 4 } = {}) {
  const tokens = toTokens(query);
  if (!tokens.length) return { 찾은개수: 0, 결과: [], 안내: '무엇을 찾을지 알 수 없습니다. 사용자가 한 말에서 핵심 낱말을 뽑아 다시 부르세요.' };

  const books = readyManuals();
  const loaded = await Promise.all(books.map(async (m) => {
    try {
      return { manual: m, sections: await loadManualSections(m.id) };
    } catch (error) {
      return { manual: m, sections: [], error: error?.message || '읽기 실패' };
    }
  }));

  const scored = [];
  for (const { manual, sections } of loaded) {
    for (const s of sections) {
      const score = scoreSection({ manualTitle: manual.title, ...s }, tokens);
      if (score > 0) scored.push({ score, manual, section: s });
    }
  }
  scored.sort((a, b) => b.score - a.score);

  const top = scored.slice(0, limit).map(({ manual, section }) => ({
    매뉴얼: manual.title,
    매뉴얼id: manual.id,
    장: section.번호 ? `${section.번호} ${section.제목}` : section.제목,
    장id: section.장id,
    발췌: excerptAround(section.본문, tokens),
    링크: manualLink(manual.id, section.장id),
  }));

  const failed = loaded.filter((l) => l.error).map((l) => l.manual.title);

  return {
    찾은개수: top.length,
    결과: top,
    ...(failed.length ? { 못읽은매뉴얼: failed } : {}),
    ...(top.length
      ? { 안내: '발췌만으로 답하기 어려우면 read_manual 로 그 장을 통째로 읽으세요. 답 끝에 링크를 붙여 주세요.' }
      : { 안내: '매뉴얼에서 못 찾았습니다. 다른 말로 한 번 더 찾아보고, 그래도 없으면 매뉴얼에 없는 내용이라고 밝히고 답하세요.' }),
  };
}

/**
 * 장 하나를 통째로 읽는다. 장을 지정하지 않으면 그 매뉴얼의 목차를 준다.
 */
export async function readManual(manualId, 장id) {
  const manual = findManual(manualId);
  if (!manual) return { 오류: `'${manualId}' 라는 매뉴얼은 없습니다. search_manual 로 먼저 찾으세요.` };
  if (manual.status !== MANUAL_STATUS.READY) {
    return { 오류: `${manual.title} 매뉴얼은 아직 준비 중입니다.`, 안내: '이 화면은 매뉴얼이 없다고 사용자에게 알려주세요.' };
  }

  let sections;
  try {
    sections = await loadManualSections(manual.id);
  } catch (error) {
    return { 오류: error?.message || '매뉴얼을 불러오지 못했습니다.' };
  }

  const 목차 = sections.map((s) => ({ 장id: s.장id, 장: s.번호 ? `${s.번호} ${s.제목}` : s.제목 }));

  if (!장id) {
    return { 매뉴얼: manual.title, 매뉴얼id: manual.id, 목차, 링크: manualLink(manual.id) };
  }

  const found = sections.find((s) => s.장id === String(장id));
  if (!found) {
    return { 오류: `${manual.title} 에 '${장id}' 라는 장이 없습니다.`, 목차 };
  }

  const 본문 = found.본문.length > MAX_SECTION_CHARS
    ? `${found.본문.slice(0, MAX_SECTION_CHARS)}\n…(이 장은 길어서 잘렸습니다. 나머지는 링크에서 볼 수 있습니다.)`
    : found.본문;

  return {
    매뉴얼: manual.title,
    장: found.번호 ? `${found.번호} ${found.제목}` : found.제목,
    내용: 본문,
    링크: manualLink(manual.id, found.장id),
  };
}

/**
 * 프롬프트에 넣을 짧은 목록.
 * 장 제목까지는 넣지 않는다 — 매뉴얼이 6권이라 장을 다 넣으면 50줄이 매 턴 나간다.
 */
export function buildManualHint() {
  const ready = readyManuals().map((m) => m.title);
  const planned = MANUALS.filter((m) => m.status !== MANUAL_STATUS.READY).map((m) => m.title);
  if (!ready.length) return '';
  return [
    `[매뉴얼] 화면 사용법은 아래 매뉴얼에 있습니다. search_manual 로 찾아 그 내용을 근거로 답하세요.`,
    `- 있는 매뉴얼: ${ready.join(', ')}`,
    ...(planned.length ? [`- 아직 없는 매뉴얼: ${planned.join(', ')} (물어보면 준비 중이라고 답하세요)`] : []),
  ].join('\n');
}
